<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="headline font-weight-bold">Rutinas de Entrenamiento</span>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="openCreateRoutineDialog" class="mr-2">
                        <v-icon left>mdi-plus</v-icon>
                        Nueva Rutina
                    </v-btn>
                </v-card-title>

                <v-alert v-if="errorMessage" type="error" dense outlined dismissible @input="errorMessage = ''">
                    {{ errorMessage }}
                </v-alert>

                <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
                    <v-tab>
                        <v-icon left>mdi-dumbbell</v-icon>
                        Mis Rutinas
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-book-open</v-icon>
                        Biblioteca del Club
                    </v-tab>
                </v-tabs>

                <v-progress-linear v-if="loading" indeterminate class="mb-4"></v-progress-linear>

                <v-tabs-items v-model="activeTab" class="transparent">
                    <!-- Tab 1: Mis Rutinas -->
                    <v-tab-item>
                        <v-row>
                            <v-col v-for="routine in myRoutines" :key="routine.id" cols="12" sm="6" md="4">
                                <v-card class="elevation-2 h-100">
                                    <v-card-title class="pb-2">
                                        <div class="headline">{{ routine.name }}</div>
                                    </v-card-title>
                                    <v-card-subtitle class="pb-2">{{ routine.description }}</v-card-subtitle>
                                    <v-divider></v-divider>
                                    <v-card-text class="pa-4">
                                        <div class="mb-3">
                                            <div class="caption grey--text">Ejercicios / Series</div>
                                            <div class="body-1 font-weight-bold">
                                                {{ routine.exercises.length }} / {{ routine.total_sets }}
                                            </div>
                                        </div>
                                        <div class="mb-3">
                                            <div class="caption grey--text">Creada</div>
                                            <div class="body-2">{{ formatDate(routine.created_at) }}</div>
                                        </div>
                                    </v-card-text>
                                    <v-card-actions>
                                        <v-btn small text color="primary" @click="openEditRoutine(routine)"
                                               :disabled="!routine.can_edit">
                                            <v-icon small left>mdi-pencil</v-icon>
                                            Editar
                                        </v-btn>
                                        <v-btn small text color="info" @click="viewRoutineDetails(routine)">
                                            <v-icon small left>mdi-eye</v-icon>
                                            Ver
                                        </v-btn>
                                        <v-spacer></v-spacer>
                                        <v-btn small text color="error" @click="confirmDelete(routine)"
                                               :disabled="!routine.can_edit">
                                            <v-icon small>mdi-delete</v-icon>
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-row v-if="!loading && myRoutines.length === 0" class="justify-center py-8">
                            <v-col cols="12" class="text-center">
                                <v-icon size="48" class="grey--text mb-2">mdi-dumbbell</v-icon>
                                <div class="subtitle-1 grey--text">Sin rutinas creadas</div>
                                <v-btn color="primary" text @click="openCreateRoutineDialog" class="mt-2">
                                    <v-icon left>mdi-plus</v-icon>
                                    Crear primera rutina
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <!-- Tab 2: Biblioteca del club -->
                    <v-tab-item>
                        <v-row>
                            <v-col v-for="routine in sharedRoutines" :key="routine.id" cols="12" sm="6" md="4">
                                <v-card class="elevation-2 h-100">
                                    <v-card-title class="pb-2">
                                        <div class="headline">{{ routine.name }}</div>
                                    </v-card-title>
                                    <v-card-subtitle class="pb-2">{{ routine.description }}</v-card-subtitle>
                                    <v-divider></v-divider>
                                    <v-card-text class="pa-4">
                                        <div class="mb-3">
                                            <div class="caption grey--text">Creada por</div>
                                            <div class="body-2 font-weight-bold">
                                                {{ routine.created_by ? routine.created_by.name : 'Plantilla del club' }}
                                            </div>
                                        </div>
                                        <div class="mb-3">
                                            <div class="caption grey--text">Ejercicios / Series</div>
                                            <div class="body-1 font-weight-bold">
                                                {{ routine.exercises.length }} / {{ routine.total_sets }}
                                            </div>
                                        </div>
                                    </v-card-text>
                                    <v-card-actions>
                                        <v-btn small text color="info" @click="viewRoutineDetails(routine)">
                                            <v-icon small left>mdi-eye</v-icon>
                                            Ver
                                        </v-btn>
                                        <v-btn small text color="primary" v-if="routine.can_edit"
                                               @click="openEditRoutine(routine)">
                                            <v-icon small left>mdi-pencil</v-icon>
                                            Editar
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-row v-if="!loading && sharedRoutines.length === 0" class="justify-center py-8">
                            <v-col cols="12" class="text-center grey--text">Sin rutinas de otros coaches</v-col>
                        </v-row>
                    </v-tab-item>
                </v-tabs-items>
            </v-flex>
        </v-layout>

        <!-- Diálogo: Crear/Editar Rutina -->
        <v-dialog v-model="createRoutineDialog" max-width="800px" persistent scrollable>
            <v-card v-if="editingRoutine">
                <v-card-title>
                    <div>
                        <div class="headline">{{ isEditingRoutine ? 'Editar Rutina' : 'Nueva Rutina de Entrenamiento' }}</div>
                        <div class="caption grey--text">Configura los detalles de la rutina</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="createRoutineDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-alert v-if="formError" type="error" dense outlined>{{ formError }}</v-alert>
                    <v-form ref="routineForm" v-model="validRoutineForm" lazy-validation>
                        <v-row>
                            <v-col cols="12" sm="8">
                                <v-text-field
                                    v-model="editingRoutine.name"
                                    label="Nombre de la Rutina"
                                    outlined dense counter maxlength="80"
                                    :rules="[v => !!(v || '').trim() || 'Se requiere un nombre']"
                                    placeholder="Ej: Pecho y Tríceps"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="editingRoutine.focus"
                                    :items="focusOptions"
                                    item-text="label"
                                    item-value="code"
                                    label="Enfoque"
                                    outlined dense
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="8">
                                <v-textarea
                                    v-model="editingRoutine.description"
                                    label="Descripción"
                                    outlined dense rows="2" counter maxlength="300"
                                    placeholder="Describe el objetivo de esta rutina"
                                ></v-textarea>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model.number="editingRoutine.estimated_minutes"
                                    label="Duración estimada (min)"
                                    type="number" outlined dense
                                    :rules="[v => (v >= 5 && v <= 300) || 'Entre 5 y 300']"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4"></v-divider>

                        <div class="subtitle-2 font-weight-bold mb-4">
                            <v-icon small class="mr-2">mdi-dumbbell</v-icon>
                            Ejercicios ({{ editingRoutine.exercises.length }})
                            <v-btn small text color="primary" @click="openAddExerciseDialog" class="ml-2"
                                   :disabled="editingRoutine.exercises.length >= 30">
                                <v-icon small left>mdi-plus</v-icon>
                                Agregar Ejercicio
                            </v-btn>
                        </div>

                        <v-list dense v-if="editingRoutine.exercises.length > 0">
                            <v-list-item v-for="(exercise, idx) in editingRoutine.exercises" :key="idx" class="mb-2">
                                <v-card class="w-100">
                                    <v-card-text class="pa-3">
                                        <v-row align="center" class="mb-1">
                                            <v-col cols="auto">
                                                <span class="body-2 font-weight-bold">{{ idx + 1 }}. {{ exercise.exercise_name }}</span>
                                            </v-col>
                                            <v-spacer></v-spacer>
                                            <v-col cols="auto">
                                                <v-btn x-small icon :disabled="idx === 0" @click="moveExercise(idx, -1)">
                                                    <v-icon x-small>mdi-arrow-up</v-icon>
                                                </v-btn>
                                                <v-btn x-small icon :disabled="idx === editingRoutine.exercises.length - 1"
                                                       @click="moveExercise(idx, 1)">
                                                    <v-icon x-small>mdi-arrow-down</v-icon>
                                                </v-btn>
                                                <v-btn x-small icon color="primary" @click="openEditExerciseDialog(idx)">
                                                    <v-icon x-small>mdi-pencil</v-icon>
                                                </v-btn>
                                                <v-btn x-small icon color="error" @click="removeExercise(idx)">
                                                    <v-icon x-small>mdi-delete</v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                        <div class="caption">{{ setsSummary(exercise) }}</div>
                                        <div class="caption grey--text" v-if="exercise.notes">{{ exercise.notes }}</div>
                                    </v-card-text>
                                </v-card>
                            </v-list-item>
                        </v-list>

                        <v-card v-else class="grey lighten-5 pa-4 text-center">
                            <v-icon class="grey--text mb-2" size="32">mdi-alert-circle</v-icon>
                            <div class="body-2 grey--text">Sin ejercicios agregados</div>
                        </v-card>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="createRoutineDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveRoutine" :loading="saving">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar Rutina
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Agregar/Editar Ejercicio -->
        <v-dialog v-model="exerciseDialog" max-width="600px" persistent>
            <v-card v-if="editingExercise">
                <v-card-title>
                    <div class="headline">{{ isEditingExercise ? 'Editar Ejercicio' : 'Nuevo Ejercicio' }}</div>
                    <v-spacer></v-spacer>
                    <v-icon @click="exerciseDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="exerciseForm" v-model="validExerciseForm" lazy-validation>
                        <v-autocomplete
                            v-model="editingExercise.exercise_id"
                            :items="exerciseItems"
                            :loading="loadingExercises"
                            :search-input.sync="exerciseSearch"
                            item-text="name"
                            item-value="id"
                            label="Ejercicio del catálogo"
                            no-filter
                            outlined dense
                            :rules="[v => !!v || 'Selecciona un ejercicio del catálogo']"
                            @change="onExercisePicked"
                        >
                            <template v-slot:item="{ item }">
                                <div>
                                    <div class="body-2">{{ item.name }}</div>
                                    <div class="caption grey--text">{{ muscleLabel(item.primary_muscle) }} • {{ item.equipment }}</div>
                                </div>
                            </template>
                        </v-autocomplete>

                        <v-row>
                            <v-col cols="6" sm="3">
                                <v-text-field v-model.number="editingExercise.series" label="Series" type="number"
                                              outlined dense
                                              :rules="[v => (v >= 1 && v <= 20) || '1 a 20']"></v-text-field>
                            </v-col>
                            <v-col cols="6" sm="3">
                                <v-text-field v-model.number="editingExercise.reps" label="Repeticiones" type="number"
                                              outlined dense
                                              :rules="[v => (v >= 0 && v <= 999) || '0 a 999']"></v-text-field>
                            </v-col>
                            <v-col cols="6" sm="3">
                                <v-text-field v-model.number="editingExercise.weight" label="Peso (kg)" type="number"
                                              outlined dense
                                              :rules="[v => (v >= 0 && v <= 1000) || '0 a 1000']"></v-text-field>
                            </v-col>
                            <v-col cols="6" sm="3">
                                <v-text-field v-model.number="editingExercise.rest_seconds" label="Descanso (seg)"
                                              type="number" outlined dense
                                              :rules="[v => (v >= 0 && v <= 600) || '0 a 600']"></v-text-field>
                            </v-col>
                        </v-row>

                        <v-alert v-if="editingExercise.nonUniform" type="info" dense outlined>
                            Este ejercicio tiene series con valores distintos. Al guardar, todas las series
                            tomarán estos valores.
                        </v-alert>

                        <v-textarea
                            v-model="editingExercise.notes"
                            label="Notas especiales"
                            outlined dense rows="2" counter maxlength="300"
                            placeholder="Ej: Controlar la bajada, evitar rebote"
                        ></v-textarea>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="exerciseDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveExercise">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar Ejercicio
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Ver Detalles de Rutina -->
        <v-dialog v-model="detailsDialog" max-width="800px" scrollable>
            <v-card v-if="viewingRoutine">
                <v-card-title>
                    <div>
                        <div class="headline">{{ viewingRoutine.name }}</div>
                        <div class="caption grey--text">
                            {{ viewingRoutine.created_by ? 'por ' + viewingRoutine.created_by.name : 'Plantilla del club' }}
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="detailsDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <div class="mb-4" v-if="viewingRoutine.description">
                        <div class="subtitle-2 font-weight-bold mb-2">Descripción</div>
                        <div class="body-2">{{ viewingRoutine.description }}</div>
                    </div>
                    <v-divider class="my-4" v-if="viewingRoutine.description"></v-divider>
                    <div class="subtitle-2 font-weight-bold mb-3">Ejercicios ({{ viewingRoutine.exercises.length }})</div>
                    <v-list v-if="viewingRoutine.exercises.length > 0" dense>
                        <v-list-item v-for="(exercise, idx) in viewingRoutine.exercises" :key="idx" class="mb-3">
                            <v-card class="w-100 grey lighten-4">
                                <v-card-text class="pa-3">
                                    <div class="body-2 font-weight-bold mb-1">{{ idx + 1 }}. {{ exercise.exercise_name }}</div>
                                    <div class="caption">{{ setsSummary(exercise) }}</div>
                                    <div v-if="exercise.notes" class="caption grey--text mt-1">
                                        <strong>Notas:</strong> {{ exercise.notes }}
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Diálogo: confirmar eliminación -->
        <v-dialog v-model="deleteDialog" max-width="420px">
            <v-card v-if="routineToDelete">
                <v-card-title class="headline">Eliminar rutina</v-card-title>
                <v-card-text>
                    ¿Eliminar "{{ routineToDelete.name }}"? Las rutinas ya asignadas a socios se conservan.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
                    <v-btn color="error" text @click="deleteRoutine" :loading="saving">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
import {
  apiErrorMessage, createLibraryRoutine, deleteLibraryRoutine, formatDate, getExercises, getFitnessMeta,
  getLibraryRoutines, updateLibraryRoutine
} from '@/services/gym'

const DEFAULT_FOCUSES = [{ code: 'custom', label: 'Personalizado' }]

const emptyRoutine = () => ({
  id: null, name: '', description: '', focus: 'custom', estimated_minutes: 60, exercises: []
})

const emptyExercise = () => ({
  exercise_id: null, exercise_name: '', series: 3, reps: 10, weight: 0, rest_seconds: 60, notes: '', nonUniform: false
})

export default {
  name: 'CoachRoutines',
  data: () => ({
    activeTab: 0,
    loading: false,
    saving: false,
    errorMessage: '',
    formError: '',
    allRoutines: [],
    mineIds: [],
    focusOptions: DEFAULT_FOCUSES,
    muscles: {},

    createRoutineDialog: false,
    exerciseDialog: false,
    detailsDialog: false,
    deleteDialog: false,
    validRoutineForm: false,
    validExerciseForm: false,
    editingRoutine: null,
    editingExercise: null,
    editingExerciseIndex: null,
    viewingRoutine: null,
    routineToDelete: null,
    isEditingRoutine: false,
    isEditingExercise: false,

    exerciseItems: [],
    exerciseSearch: '',
    loadingExercises: false,
    exerciseTimer: null,

    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success'
  }),
  computed: {
    myRoutines () {
      return this.allRoutines.filter(r => this.mineIds.includes(r.id))
    },
    sharedRoutines () {
      return this.allRoutines.filter(r => !this.mineIds.includes(r.id))
    }
  },
  watch: {
    exerciseSearch (value) {
      const selected = this.exerciseItems.find(e => e.id === (this.editingExercise || {}).exercise_id)
      if (selected && selected.name === value) return
      clearTimeout(this.exerciseTimer)
      this.exerciseTimer = setTimeout(() => this.searchExercises(value), 300)
    }
  },
  created () {
    this.loadMeta()
    this.loadRoutines()
  },
  beforeDestroy () {
    clearTimeout(this.exerciseTimer)
  },
  methods: {
    formatDate,
    notify (message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    async loadMeta () {
      try {
        const { data } = await getFitnessMeta()
        this.focusOptions = data.routine_focuses
        this.muscles = data.muscle_groups.reduce((acc, m) => Object.assign(acc, { [m.code]: m.label }), {})
      } catch (error) {
        // Sin metadatos la vista sigue funcionando con valores por defecto
      }
    },
    muscleLabel (code) {
      return this.muscles[code] || code
    },
    async loadRoutines () {
      this.loading = true
      try {
        const [all, mine] = await Promise.all([
          getLibraryRoutines({ page_size: 200 }),
          getLibraryRoutines({ page_size: 200, mine: true })
        ])
        this.allRoutines = all.data.results
        this.mineIds = mine.data.results.map(r => r.id)
      } catch (error) {
        this.errorMessage = apiErrorMessage(error)
      } finally {
        this.loading = false
      }
    },
    setsSummary (exercise) {
      const sets = exercise.sets || []
      if (!sets.length) return 'Sin series'
      const first = sets[0]
      const uniform = sets.every(s => s.reps === first.reps && Number(s.weight) === Number(first.weight))
      const weight = first.weight ? ` @ ${first.weight} kg` : ''
      if (uniform) return `${sets.length} series × ${first.reps === null ? '—' : first.reps} reps${weight} • descanso ${first.rest_seconds}s`
      return sets.map(s => `${s.reps === null ? '—' : s.reps}${s.weight ? `×${s.weight}kg` : ''}`).join(' / ') +
        ` • ${sets.length} series`
    },

    // ---------- rutina
    openCreateRoutineDialog () {
      this.isEditingRoutine = false
      this.editingRoutine = emptyRoutine()
      this.formError = ''
      this.createRoutineDialog = true
      this.$nextTick(() => this.$refs.routineForm && this.$refs.routineForm.resetValidation())
    },
    openEditRoutine (routine) {
      this.isEditingRoutine = true
      this.formError = ''
      this.editingRoutine = {
        id: routine.id,
        name: routine.name,
        description: routine.description || '',
        focus: routine.focus,
        estimated_minutes: routine.estimated_minutes,
        exercises: JSON.parse(JSON.stringify(routine.exercises))
      }
      this.createRoutineDialog = true
    },
    async saveRoutine () {
      if (!this.$refs.routineForm.validate()) return
      if (this.editingRoutine.exercises.length === 0) {
        this.formError = 'Agrega al menos un ejercicio a la rutina.'
        return
      }
      this.saving = true
      this.formError = ''
      const r = this.editingRoutine
      const body = {
        name: r.name.trim(),
        description: r.description || null,
        focus: r.focus,
        estimated_minutes: r.estimated_minutes,
        exercises: r.exercises.map((e, i) => ({
          exercise_id: e.exercise_id,
          order: i,
          notes: e.notes || null,
          superset_group_id: e.superset_group_id || null,
          sets: e.sets.map((s, n) => ({
            set_number: n + 1,
            reps: s.reps,
            weight: s.weight,
            duration_seconds: s.duration_seconds,
            rest_seconds: s.rest_seconds,
            is_warmup: s.is_warmup,
            is_drop_set: s.is_drop_set
          }))
        }))
      }
      try {
        if (this.isEditingRoutine) {
          await updateLibraryRoutine(r.id, body)
        } else {
          await createLibraryRoutine(body)
        }
        this.createRoutineDialog = false
        this.notify(this.isEditingRoutine ? 'Rutina actualizada correctamente' : 'Rutina creada correctamente')
        this.loadRoutines()
      } catch (error) {
        this.formError = apiErrorMessage(error)
      } finally {
        this.saving = false
      }
    },
    viewRoutineDetails (routine) {
      this.viewingRoutine = routine
      this.detailsDialog = true
    },
    confirmDelete (routine) {
      this.routineToDelete = routine
      this.deleteDialog = true
    },
    async deleteRoutine () {
      this.saving = true
      try {
        await deleteLibraryRoutine(this.routineToDelete.id)
        this.deleteDialog = false
        this.notify('Rutina eliminada', 'info')
        this.loadRoutines()
      } catch (error) {
        this.notify(apiErrorMessage(error), 'error')
      } finally {
        this.saving = false
      }
    },

    // ---------- ejercicios de la rutina
    async searchExercises (search) {
      this.loadingExercises = true
      try {
        const { data } = await getExercises({ search: search || undefined, page_size: 30 })
        const current = this.exerciseItems.find(e => e.id === (this.editingExercise || {}).exercise_id)
        this.exerciseItems = data.results.concat(
          current && !data.results.some(e => e.id === current.id) ? [current] : [])
      } catch (error) {
        this.notify(apiErrorMessage(error), 'error')
      } finally {
        this.loadingExercises = false
      }
    },
    onExercisePicked (id) {
      const picked = this.exerciseItems.find(e => e.id === id)
      if (picked) this.editingExercise.exercise_name = picked.name
    },
    openAddExerciseDialog () {
      this.isEditingExercise = false
      this.editingExerciseIndex = null
      this.editingExercise = emptyExercise()
      this.exerciseItems = []
      this.exerciseSearch = ''
      this.searchExercises('')
      this.exerciseDialog = true
      this.$nextTick(() => this.$refs.exerciseForm && this.$refs.exerciseForm.resetValidation())
    },
    openEditExerciseDialog (index) {
      const e = this.editingRoutine.exercises[index]
      const first = e.sets[0] || {}
      this.isEditingExercise = true
      this.editingExerciseIndex = index
      this.editingExercise = {
        exercise_id: e.exercise_id,
        exercise_name: e.exercise_name,
        series: e.sets.length || 1,
        reps: first.reps === undefined || first.reps === null ? 10 : first.reps,
        weight: first.weight || 0,
        rest_seconds: first.rest_seconds === undefined ? 60 : first.rest_seconds,
        notes: e.notes || '',
        nonUniform: e.sets.some(s => s.reps !== first.reps || Number(s.weight) !== Number(first.weight) ||
          s.rest_seconds !== first.rest_seconds),
        base: e
      }
      this.exerciseItems = e.exercise_id ? [{ id: e.exercise_id, name: e.exercise_name }] : []
      this.exerciseSearch = e.exercise_name
      this.exerciseDialog = true
    },
    saveExercise () {
      if (!this.$refs.exerciseForm.validate()) return
      const f = this.editingExercise
      const base = f.base || {}
      const item = {
        exercise_id: f.exercise_id,
        exercise_name: f.exercise_name,
        notes: f.notes || null,
        superset_group_id: base.superset_group_id || null,
        sets: Array.from({ length: f.series }, (_, i) => ({
          set_number: i + 1,
          reps: f.reps,
          weight: f.weight || 0,
          duration_seconds: null,
          rest_seconds: f.rest_seconds,
          is_warmup: false,
          is_drop_set: false
        }))
      }
      if (this.isEditingExercise) {
        this.$set(this.editingRoutine.exercises, this.editingExerciseIndex, item)
      } else {
        this.editingRoutine.exercises.push(item)
      }
      this.exerciseDialog = false
    },
    moveExercise (index, delta) {
      const list = this.editingRoutine.exercises
      const target = index + delta
      if (target < 0 || target >= list.length) return
      const [item] = list.splice(index, 1)
      list.splice(target, 0, item)
    },
    removeExercise (index) {
      this.editingRoutine.exercises.splice(index, 1)
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.w-100 {
  width: 100%;
}

.h-100 {
  height: 100%;
}
</style>
